import { useEffect, useRef } from 'react';

const TELEGRAM_LOGIN_ENABLED =
  process.env.REACT_APP_FEATURE_TELEGRAM_LOGIN === 'true';
const TELEGRAM_CLIENT_ID = process.env.REACT_APP_TELEGRAM_CLIENT_ID || '';
const LOG_PREFIX = '[TelegramLoginButton]';

type TelegramLoginResult =
  | { success: true; idToken: string }
  | { success: false; error: 'cancelled' | 'phone_denied' | 'error' };

interface Props {
  onResult: (result: TelegramLoginResult) => void;
  disabled?: boolean;
}

let telegramScriptPromise: Promise<void> | null = null;

const logInfo = (...args: unknown[]) => {
  console.info(LOG_PREFIX, ...args);
};

const logWarn = (...args: unknown[]) => {
  console.warn(LOG_PREFIX, ...args);
};

const logError = (...args: unknown[]) => {
  console.error(LOG_PREFIX, ...args);
};

declare global {
  interface Window {
    Telegram?: {
      Login: {
        init(
          options: {
            client_id: string;
            request_access: string[];
          },
          callback: (
            result: false | { id_token: string; [key: string]: unknown }
          ) => void
        ): void;
      };
    };
  }
}

const ensureTelegramScript = () => {
  if (window.Telegram?.Login) {
    logInfo('SDK already available on window.Telegram.Login');
    return Promise.resolve();
  }

  if (telegramScriptPromise) {
    logInfo('Reusing in-flight SDK loading promise');
    return telegramScriptPromise;
  }

  telegramScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://oauth.telegram.org/js/telegram-login.js"]'
    );

    if (existingScript) {
      logInfo('Found existing Telegram SDK script tag, waiting for load');
      existingScript.addEventListener('load', () => resolve(), {
        once: true
      });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Telegram SDK')),
        { once: true }
      );

      if (window.Telegram?.Login) {
        logInfo('Existing script already loaded and SDK ready');
        resolve();
      }

      return;
    }

    logInfo('Injecting Telegram SDK script');
    const script = document.createElement('script');
    script.src = 'https://oauth.telegram.org/js/telegram-login.js';
    script.async = true;
    script.onload = () => {
      logInfo('Telegram SDK script loaded');
      resolve();
    };
    script.onerror = () => {
      logError('Failed to load Telegram SDK script');
      reject(new Error('Failed to load Telegram SDK'));
    };
    document.head.appendChild(script);
  });

  return telegramScriptPromise;
};

/**
 * Renders a "Log in with Telegram" button.
 * The component is a no-op (renders nothing) when the feature flag is off.
 *
 * Lazily loads https://oauth.telegram.org/js/telegram-login.js on first render,
 * then calls Telegram.Login.init with request_access: ['phone'] when the user
 * clicks the button.
 */
export default function TelegramLoginButton({ onResult, disabled }: Props) {
  const scriptRequested = useRef(false);

  useEffect(() => {
    if (!TELEGRAM_LOGIN_ENABLED || scriptRequested.current) return;

    scriptRequested.current = true;
    logInfo('Preloading Telegram SDK', {
      enabled: TELEGRAM_LOGIN_ENABLED,
      hasClientId: Boolean(TELEGRAM_CLIENT_ID)
    });
    void ensureTelegramScript();
  }, []);

  if (!TELEGRAM_LOGIN_ENABLED || !TELEGRAM_CLIENT_ID) {
    return null;
  }

  const handleClick = () => {
    logInfo('Button clicked, starting Telegram login init');

    if (!window.Telegram?.Login) {
      logWarn(
        'window.Telegram.Login unavailable at click time; SDK may still be loading'
      );
      void ensureTelegramScript();
      onResult({ success: false, error: 'error' });
      return;
    }

    let callbackCalled = false;
    const callbackTimeout = window.setTimeout(() => {
      if (!callbackCalled) {
        logWarn(
          'Telegram callback not received within 8s. Possible popup block, domain mismatch, or Telegram-side rejection.'
        );
      }
    }, 8000);

    logInfo('Calling Telegram.Login.init');
    window.Telegram.Login.init(
      { client_id: TELEGRAM_CLIENT_ID, request_access: ['phone'] },
      (result) => {
        callbackCalled = true;
        window.clearTimeout(callbackTimeout);

        if (!result) {
          logWarn('User cancelled Telegram popup or popup closed');
          onResult({ success: false, error: 'cancelled' });
          return;
        }

        const idToken = result['id_token'];
        if (!idToken || typeof idToken !== 'string') {
          logWarn('Telegram callback missing id_token', {
            keys: Object.keys(result)
          });
          onResult({ success: false, error: 'phone_denied' });
          return;
        }

        logInfo('Telegram callback received id_token', {
          tokenLength: idToken.length
        });
        onResult({ success: true, idToken });
      }
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {/* Telegram logo SVG */}
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="12" fill="#2CA5E0" />
        <path
          d="M17.79 7.27 5.54 11.96c-.83.33-.82.8-.15 1l3.13.97 7.24-4.56c.34-.21.65-.1.4.13l-5.86 5.29-.22 3.23c.33 0 .47-.15.65-.32l1.56-1.52 3.26 2.4c.6.33 1.03.16 1.18-.55l2.13-10.04c.22-.88-.34-1.28-.97-1.02Z"
          fill="#fff"
        />
      </svg>
      Log in with Telegram
    </button>
  );
}
